import {CloudObject, Query, QueryResult, registerBrick, ErrorFlow, GlobalProperties} from 'olympe';
import {ReactBrick, useProperty, getLogger, jsonToSxProps, cssToSxProps, ifNotNull, ifNotTransparent,} from '@olympeio/core'
import React, {useRef} from "react";
import {Chart} from 'chart.js';
import {combineLatest, debounceTime, merge, of} from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { parseJSON } from "./utils/utils";
import {merge as lodashDeepMerge} from 'lodash';

import Box from '@mui/material/Box';
import PolarConfig from "./utils/PolarConfig";
import {
    interpolateBlues,
    interpolateBrBG,
    interpolateBuGn,
    interpolateBuPu,
    interpolateCividis,
    interpolateCool,
    interpolateCubehelixDefault,
    interpolateGnBu,
    interpolateGreens,
    interpolateGreys,
    interpolateInferno,
    interpolateMagma,
    interpolateOrRd,
    interpolateOranges,
    interpolatePRGn,
    interpolatePiYG,
    interpolatePlasma,
    interpolatePuBu,
    interpolatePuBuGn,
    interpolatePuOr,
    interpolatePuRd,
    interpolatePurples,
    interpolateRainbow,
    interpolateRdBu,
    interpolateRdGy,
    interpolateRdPu,
    interpolateRdYlBu,
    interpolateRdYlGn,
    interpolateReds,
    interpolateSinebow,
    interpolateSpectral,
    interpolateTurbo,
    interpolateViridis,
    interpolateWarm,
    interpolateYlGn,
    interpolateYlGnBu,
    interpolateYlOrBr,
    interpolateYlOrRd,
    schemeCategory10,
    schemeAccent,
    schemeDark2,
    schemePaired,
    schemePastel1,
    schemePastel2,
    schemeSet1,
    schemeSet2,
    schemeSet3,
    schemeTableau10
} from 'd3-scale-chromatic';

const lookup = {
    interpolateBlues,
    interpolateBrBG,
    interpolateBuGn,
    interpolateBuPu,
    interpolateCividis,
    interpolateCool,
    interpolateCubehelixDefault,
    interpolateGnBu,
    interpolateGreens,
    interpolateGreys,
    interpolateInferno,
    interpolateMagma,
    interpolateOrRd,
    interpolateOranges,
    interpolatePRGn,
    interpolatePiYG,
    interpolatePlasma,
    interpolatePuBu,
    interpolatePuBuGn,
    interpolatePuOr,
    interpolatePuRd,
    interpolatePurples,
    interpolateRainbow,
    interpolateRdBu,
    interpolateRdGy,
    interpolateRdPu,
    interpolateRdYlBu,
    interpolateRdYlGn,
    interpolateReds,
    interpolateSinebow,
    interpolateSpectral,
    interpolateTurbo,
    interpolateViridis,
    interpolateWarm,
    interpolateYlGn,
    interpolateYlGnBu,
    interpolateYlOrBr,
    interpolateYlOrRd,
    schemeCategory10,
    schemeAccent,
    schemeDark2,
    schemePaired,
    schemePastel1,
    schemePastel2,
    schemeSet1,
    schemeSet2,
    schemeSet3,
    schemeTableau10
};

// Make the objects available by their name exposed in an enum (So as a string, without the "interpolate" part)
Object.keys(lookup).forEach((key) => {
    if (key.startsWith('interpolate')) {
        lookup[`${key.charAt(11).toLowerCase()}${key.slice(12)}`] = lookup[key];
        lookup[key.slice(11)] = lookup[key];
    }
});
export default class PolarChart extends ReactBrick {
    static HARDCODED_COLOR_UNUSED = '#00000001';

    /**
     * Subscribe to the config datasets to read the configuration of the brick
     * Then subscribe to the data as well to fill the chart js object with the data
     * (This last step is done in the processData method)
     * Finally set the context variable 'graph data' with the chart js dataset object
     * 
     * By overriding init() we can set the context variable used in the rendering of the React component
     * and we don't need to override the setupExecution() since Visual Component are rendered only once
     * and then react takes care of further updates
     *
     * @override
     */
    init($) {
        this.logger = getLogger('PolarChart');
        let modelName = '';
        $.observe('config', false).pipe(
            // Read the instances of the config type provided
            switchMap((configType) => {
                if (configType?.query().followRecursively(CloudObject.extendRel).executeFromCache().has('018d9cfba982c382bd4a')) {
                    modelName = configType.name();
                    return configType ? Query.instancesOf(configType, false).observe($) : of(QueryResult.empty());
                } else {
                    if (!! configType) {
                        if (!$.get(GlobalProperties.EDITION, true)) {
                            throw ErrorFlow.create('The config provided to the chart should extend the Polar Chart Config object', 1);
                        } else {
                            this.logger.error('The config provided to the chart should extend the Polar Chart Config object');
                        }
                    }
                    return of(QueryResult.empty());
                }
            }),

            // Put the trigger to redisplay anytime any config prop change, also when the user specify manually to refresh the chart
            // When we switch axis, we must compute the data differently, so we listen to that property as well
            switchMap((configs) => {
                if(configs.size() === 0 && modelName.trim() !== '') {
                    this.logger.warn(`The config "${modelName}" does not have any instance`);
                }
                return merge(
                    combineLatest(configs.map((config) => config.observeProperties($).pipe(map(() => config)))),
                    $.observe('Refresh', false).pipe(map(() => configs.toArray())),
                    $.observe('font family', false).pipe(map(() => configs.toArray())),
                    $.observe('font size', false).pipe(map(() => configs.toArray()))
                );
            }),

            // Add data to the configs to create the datasets
            switchMap((configs) => {
                return $.observe('data', false).pipe(
                    debounceTime(200), // Debounce to avoid multiple rendering on setup or if the data updates very quickly
                    map((data) => this.processData($, data, configs)),
                );
            })
        ).subscribe((datasets) => {
            $.set('graph data', datasets);
        });
    }



    /**
     * Mix the data and the config into the dataset structure we send to chart js
     *
     * @private
     * @param {BrickContext} $
     * @param {Map | Array} data
     * @param {Object} dataConfigs
     * @return {Object}
     */
    processData($, data, dataConfigs) {
        const result = {
            labels: [],
            datasets: []
        };

        // If no config, get a default one, for simple usage
        if (dataConfigs.length === 0) {
            dataConfigs.push(CloudObject.get(PolarConfig.DEFAULT_INSTANCE_TAG));
        } else {
            dataConfigs.sort((a,b) => {
                return a.getOrder() > b.getOrder() ? 1 : -1;
            })
        }

        // Transform our config object to a chart js comprehensive config object
        for (let j = 0; j < dataConfigs.length; j++) {
            const config = {
                name: dataConfigs[j].getName(),
                colorPath: dataConfigs[j].getColorPath(),
                label: dataConfigs[j].getLegend(),
                type: dataConfigs[j].getType(),
                keyPath: dataConfigs[j].getKeyPath(),
                valuePath: dataConfigs[j].getValuePath(),
                order: dataConfigs[j].getOrder(),
                data: [],
                backgroundColor: [],
                datalabels: {
                    anchor: dataConfigs[j].getType() === 'radar' ? 'start': 'center',
                    align: dataConfigs[j].getType() === 'radar' ? 'start': 'center',
                    borderRadius: 5,
                    color: '#FFFFFF',
                    display: dataConfigs[j].getDataLabels(),
                    font: {
                        family: $.get('font family'),
                        size: $.get('font size')
                    }
                }
            };


            // Do the sanity checks over the data before injecting it into the chart config object
            if (data) {
                let dataList;
                if (dataConfigs.length === 1 && (Array.isArray(data) || data instanceof QueryResult)) {
                    // Support for a simple list for the sake of simplicity when we don't have multiple dataset
                    dataList = data;
                } else if (data instanceof Map) {
                    dataList = data.get(dataConfigs[j].getName());
                } else if (data instanceof Object) {
                    dataList = data[dataConfigs[j].getName()];
                } else {
                    this.logger.warn('The provided data is neither a map/object nor an array/QueryResult. Can\'t display the chart');
                }

                if (!dataList) {
                    this.logger.warn(`No data has been found for the configured name "${dataConfigs[j].getName()}"`);
                } else {
                    if (!Array.isArray(dataList) && !dataList instanceof QueryResult) {
                        this.logger.warn(`The data found for the configured name "${dataConfigs[j].getName()}" is not an array nor a QueryResult. It cannot be displayed on the chart.`);
                    } else {
                        if (dataList instanceof QueryResult) {
                            dataList = dataList.toArray();
                        }

                        // Injecting the data in the chart js config object
                        for (let i = 0; i < dataList.length; i++) {
                            let item = dataList[i];
                            if (item instanceof CloudObject) {
                                item = item.toJSON();
                            }
                            const xValue = item[config.keyPath];
                            const yValue = item[config.valuePath];

                            if(!result.labels.includes(xValue)){
                                // Add items so the legends are not messed up in case of several datasets
                                while (result.labels.length > config.data.length){
                                    config.data.push(null);
                                    config.backgroundColor.push(PolarChart.HARDCODED_UNUSED_COLOR); // specific color to be retrieved when building the legends
                                }
                                result.labels.push(xValue);
                            }
                            config.data.push(yValue);

                            // Only add the backgroundColor prop if we have colorPath, otherwise default on the color set in config
                            const colorValue = config.colorPath ? item[config.colorPath] : null;
                            if (colorValue !== null && colorValue !== undefined) {
                                config.backgroundColor.push(colorValue);
                            } else {
                                if (dataConfigs[j].getColorScale()?.startsWith('scheme')){
                                    const val = i % lookup[dataConfigs[j].getColorScale()].length;
                                    config.backgroundColor.push(lookup[dataConfigs[j].getColorScale()][config.type === 'radar' ? j : val]);
                                } else {
                                    config.backgroundColor.push(lookup[dataConfigs[j].getColorScale()]((config.type === 'radar' ? j : i) / (dataList.length-1)));
                                }
                            }
                        }
                    }
                }
            }


            // Setting the dataset while taking care of having the customConfig overriding all the others props
            const customConfig = parseJSON(
                dataConfigs[j].getConfigJSON(),
                this.logger,
                'The JSON object provided to configure the cartesian chart (in the dataset config) is not valid'
            );
            result.datasets[j] = lodashDeepMerge({...config}, customConfig)

            // Cleaning the value chart js don't care for
            delete result.datasets[j].keyPath;
            delete result.datasets[j].valuePath;
            delete result.datasets[j].colorPath;
        }
        return result;
    }



     /**
     * Drawing the component itself
     *
     * @override
     * @param {BrickContext} $
     */
    static getReactComponent($){
        return () => {
            const canvasRef = useRef(null);
            const [chart, setChart] = React.useState(null);

            const data = useProperty($, 'graph data', true);
            const title = useProperty($, 'title', true);
            const legendPosition = useProperty($, 'legend position', true);
            const tooltipMode = useProperty($, 'tooltip mode', true);
            const configJson = useProperty($, 'config JSON', true);
            const animation = useProperty($, 'animation', true);

            // Build the whole configuration object if there is any change
            React.useEffect(()=>{
                const fontSize = $.get('font size');
                const fontFamily = $.get('font family');

                // check if props are ready
                const canvas = canvasRef.current
                if (!canvas || !data) return;

                const config = {
                    type: data?.datasets[0]?.type,
                    data: data,
                    options: {
                        interaction: { mode: 'index' },
                        maintainAspectRatio: false,
                        plugins: {
                            title: {
                                display: title !== null && title !== undefined && title !== '',
                                text: String(title),
                                font: {
                                    size: fontSize,
                                    family: fontFamily
                                }
                            },
                            legend: {
                                display: legendPosition !== 'none',
                                position: legendPosition,
                                labels: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    },
                                    generateLabels: data?.datasets[0]?.type !== 'radar' ? (chart) => {
                                        // Get the default label list
                                        const original = Chart.overrides.pie.plugins.legend.labels.generateLabels;
                                        const labelsOriginal = original.call(this, chart);

                                        // Build an array of colors used in the datasets of the chart,
                                        // removing the filler which only purpose is to counter a bug
                                        const datasetColors = chart.data.datasets
                                            .map((e) => e.backgroundColor)
                                            .flat()
                                            .filter(a => a !== PolarChart.HARDCODED_UNUSED_COLOR);

                                        // Modify the color and hide state of each label
                                        labelsOriginal.forEach(label => {
                                            label.fillStyle = datasetColors[label.index];
                                        });
                                        return labelsOriginal;
                                    } : undefined
                                },
                            },
                            tooltip: {
                                enabled: tooltipMode !== 'none',
                                mode: tooltipMode
                            }
                        },
                        animation: animation,
                    },
                };

                // Override the config object we built, so the dev can customize as he wish
                const customConfig = parseJSON(
                    configJson,
                    getLogger('PolarChart'),
                    'The JSON object provided to configure the cartesian chart is not valid'
                );
                lodashDeepMerge(config, customConfig)

                // When we want to draw the chart again, we need to destroy it first
                if(chart){
                    chart.destroy();
                }
                setChart(new Chart(canvas, config));
            }, [data, title, legendPosition, tooltipMode, configJson, animation]);

            // Applying the default params of the component over the div
            return <Box
                // Properties + UI
                component={'div'}
                sx={{
                    width: 1,
                    height: 1,
                    ...ifNotTransparent('borderColor', useProperty($, 'Border Color')),
                    ...ifNotNull('borderRadius', `${useProperty($, 'Border Radius')}px`, useProperty($, 'Border Radius')),
                    borderWidth: useProperty($, 'Border Width'),
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                    ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                    ...cssToSxProps(useProperty($, 'CSS Property')),
                    ...jsonToSxProps(useProperty($, 'MUI sx [json]'))
                }}
                onClick={PolarChart.clickHandler.bind(this, $, chart)}
            >
                <canvas ref={canvasRef}></canvas>
            </Box>
        }
    }



    /**
     * Triggers the on item click if the function is defined. if on item click is triggered, on click is not.
     * If there is no on item click function defined, on click is triggered all the time
     *
     * @param {BrickContext} $ the olympe context
     * @param {Chart} chart the chart js object used to compute where the user clicked
     * @param {React.MouseEvent} evt the html event on click
     */
    static clickHandler($, chart, evt) {
        const lambda = $.get('On Item Click');
        if(lambda !== null){
            const activePoints = chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
            if(activePoints.length > 0){
                const [{ datasetIndex, index }] = activePoints;

                // Handle the additional data put there to counter the bug
                let realIndex = index;
                if(datasetIndex > 0) {
                    for (let i = 0; i < index; i++) {
                        if(chart.data.datasets[datasetIndex].backgroundColor[i] === PolarChart.HARDCODED_UNUSED_COLOR){
                            realIndex--;
                        }
                    }
                }

                // Handle the different kind of input we could have
                let inputData = $.get('data');
                let clickedData;
                if (Array.isArray(inputData) || inputData instanceof QueryResult) {
                    if (inputData instanceof QueryResult) {
                        inputData = inputData.toArray();
                    }
                    clickedData = inputData[index];
                } else if (inputData instanceof Map) {
                    let list = inputData.get(chart.data.datasets[datasetIndex].name);
                    if (list instanceof QueryResult) {
                        list = list.toArray();
                    }
                    clickedData = list[realIndex];
                } else if (inputData instanceof Object) {
                    clickedData = inputData[chart.data.datasets[datasetIndex].name][realIndex];
                }

                const [ctrlFlow, data] = lambda.getInputs();
                $.runner(lambda)
                    .set(data, clickedData)
                    .trigger(ctrlFlow);
            } else {
                $.trigger('On Click');
            }
        } else {
            $.trigger('On Click');
        }

    }

}

registerBrick('018d9cc30feeab25b09a', PolarChart);